import { useEffect, useRef, useState } from 'react'
import type { MagnitudeInputProps } from './contract'
import { MAG_LABELS, BANDS, bandOf } from '../../lib/scoring'
import { ensureAudio, tick, buzz, reducedMotion } from '../../lib/sound'

/**
 * Spacecraft power dial: 270° arc, 19 detents, no wrap-around.
 * Drag anywhere on the dial, tap a detent, or use the keyboard (←/→ step, Shift+←/→ band,
 * U/K/M/B/T/Q/I jump to a band). Free drag; on release the needle springs to the nearest stop.
 */
const SWEEP = 270, START = 135 // degrees; stop 0 at 135° (SW), stop 18 at 45° (SE), through 12 o'clock
const STEP = SWEEP / 18
const deg = (pos: number) => START + pos * STEP
const rad = (d: number) => (d * Math.PI) / 180
const pt = (cx: number, cy: number, r: number, d: number) => [cx + r * Math.cos(rad(d)), cy + r * Math.sin(rad(d))] as const

function arcPath(cx: number, cy: number, r: number, from: number, to: number): string {
  const [x0, y0] = pt(cx, cy, r, from), [x1, y1] = pt(cx, cy, r, to)
  return `M ${x0} ${y0} A ${r} ${r} 0 ${to - from > 180 ? 1 : 0} 1 ${x1} ${y1}`
}

export function Dial({ value, onChange, label, sublabel, disabled }: MagnitudeInputProps) {
  const [pos, setPos] = useState<number>(value ?? 0)   // continuous 0..18 while dragging
  const posRef = useRef(pos)
  posRef.current = pos
  const svgRef = useRef<SVGSVGElement>(null)
  const drag = useRef<{ id: number; lastAngle: number; moved: boolean } | null>(null)
  const spring = useRef(0)
  const lastStop = useRef<number | null>(value)

  // external value changes (band button jump, keyboard, new question) move the needle
  useEffect(() => {
    if (value == null) { setPos(0); lastStop.current = null; return }
    if (Math.round(posRef.current) !== value || drag.current == null) animateTo(value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const settleTimer = useRef(0)
  function animateTo(target: number) {
    cancelAnimationFrame(spring.current)
    clearTimeout(settleTimer.current)
    if (reducedMotion()) { setPos(target); return }
    // rAF is suspended in hidden tabs — guarantee the needle lands regardless
    settleTimer.current = window.setTimeout(() => { cancelAnimationFrame(spring.current); setPos(target) }, 450)
    const run = () => {
      const cur = posRef.current
      const next = cur + (target - cur) * 0.28
      if (Math.abs(next - target) < 0.01) { setPos(target); return }
      setPos(next)
      spring.current = requestAnimationFrame(run)
    }
    spring.current = requestAnimationFrame(run)
  }

  function crossDetents(next: number) {
    const stop = Math.max(0, Math.min(18, Math.round(next)))
    if (stop !== lastStop.current) {
      lastStop.current = stop
      tick(stop % 3 === 0); buzz(15)
      onChange(stop)
    }
  }

  function angleAt(e: { clientX: number; clientY: number }): number {
    const b = svgRef.current!.getBoundingClientRect()
    return (Math.atan2(e.clientY - (b.top + b.height / 2), e.clientX - (b.left + b.width / 2)) * 180) / Math.PI
  }

  const onPointerDown = (e: React.PointerEvent) => {
    if (disabled) return
    ensureAudio()
    cancelAnimationFrame(spring.current)
    ;(e.currentTarget as Element).setPointerCapture(e.pointerId)
    drag.current = { id: e.pointerId, lastAngle: angleAt(e), moved: false }
  }
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current
    if (!d || e.pointerId !== d.id) return
    const a = angleAt(e)
    let delta = a - d.lastAngle
    if (delta > 180) delta -= 360      // unwrap atan2's ±π seam so crossing 12 o'clock
    if (delta < -180) delta += 360     // doesn't snap the dial a full turn backward
    d.lastAngle = a
    if (Math.abs(delta) > 0.5) d.moved = true
    const next = Math.max(0, Math.min(18, posRef.current + delta / STEP))
    setPos(next)
    crossDetents(next)
  }
  const onPointerUp = (e: React.PointerEvent) => {
    const d = drag.current
    if (!d || e.pointerId !== d.id) return
    drag.current = null
    if (!d.moved) {
      // tap-to-set: jump to the detent nearest the tap angle (within the arc)
      let a = angleAt(e)
      let rel = a - START
      while (rel < -45) rel += 360
      const stop = Math.max(0, Math.min(18, Math.round(rel / STEP)))
      lastStop.current = stop
      ensureAudio(); tick(true); buzz(15)
      onChange(stop)
      animateTo(stop)
      return
    }
    const stop = Math.max(0, Math.min(18, Math.round(posRef.current)))
    lastStop.current = stop
    onChange(stop)
    animateTo(stop) // spring settle
  }

  // keyboard: arrows step, shift+arrows jump a band, letters jump to a band
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (disabled) return
      const cur = value ?? 0
      const go = (s: number) => { const c = Math.max(0, Math.min(18, s)); lastStop.current = c; onChange(c) }
      if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { e.preventDefault(); go(cur + (e.shiftKey ? 3 : 1)) }
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { e.preventDefault(); go(cur - (e.shiftKey ? 3 : 1)) }
      else {
        const k = e.key.toLowerCase()
        const map: Record<string, number> = { u: 0, k: 3, m: 6, b: 9, t: 12, q: 15, i: 18 }
        if (k in map) go(map[k])
      }
    }
    addEventListener('keydown', onKey); return () => removeEventListener('keydown', onKey)
  }, [value, onChange, disabled])

  const C = 130, R = 104, size = 260
  const frac = pos / 18
  const band = value == null ? -1 : bandOf(value)
  const bandStops = band < 0 ? [] : band === 6 ? [18] : [BANDS[band].start, BANDS[band].start + 1, BANDS[band].start + 2]

  return (
    <div className="dial-wrap">
      <svg
        ref={svgRef} className="dial" viewBox={`0 0 ${size} ${size}`}
        onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
        role="slider" aria-label="Order of magnitude" aria-valuemin={0} aria-valuemax={18}
        aria-valuenow={value ?? 0} aria-valuetext={value == null ? 'none' : MAG_LABELS[value]}
      >
        {/* track */}
        <path d={arcPath(C, C, R, deg(0), deg(18))} className="dial-track" />
        {/* glow ring: continuous with magnitude */}
        {value != null && (
          <path d={arcPath(C, C, R, deg(0), deg(Math.max(0.01, pos)))} className="dial-glow"
            style={{ opacity: 0.3 + frac * 0.7, strokeWidth: 5 + frac * 5, filter: `drop-shadow(0 0 ${4 + frac * 10}px #B4FF39)` }} />
        )}
        {/* detent ticks: band starts are majors */}
        {MAG_LABELS.map((_, i) => {
          const major = i % 3 === 0
          const [x0, y0] = pt(C, C, R - (major ? 16 : 10), deg(i))
          const [x1, y1] = pt(C, C, R - 3, deg(i))
          return <line key={i} x1={x0} y1={y0} x2={x1} y2={y1} className={'dial-tick' + (major ? ' major' : '') + (value != null && i === value ? ' on' : '')} />
        })}
        {/* band labels around the arc at each band's middle stop */}
        {BANDS.map((b, k) => {
          const mid = k === 6 ? 18 : b.start + 1
          const [x, y] = pt(C, C, R + 14, deg(mid))
          return <text key={b.label} x={x} y={y + 3} className={'dial-band-label' + (k === band ? ' on' : '')} textAnchor="middle">{b.label}</text>
        })}
        {/* needle */}
        {value != null && (() => {
          const [x0, y0] = pt(C, C, 30, deg(pos))
          const [x1, y1] = pt(C, C, R - 20, deg(pos))
          return <g><line x1={x0} y1={y0} x2={x1} y2={y1} className="dial-needle" /><circle cx={x1} cy={y1} r="4.5" className="dial-needle-cap" /></g>
        })()}
        <circle cx={C} cy={C} r="52" className="dial-hub" />
        {/* central readout */}
        <text x={C} y={value == null ? C + 6 : C + 2} className={'dial-readout' + (value == null ? ' idle' : '')} textAnchor="middle">{value == null ? 'turn' : label}</text>
        {value != null && sublabel && <text x={C} y={C + 22} className="dial-sublabel" textAnchor="middle">{sublabel}</text>}
      </svg>
      {/* the current band's sub-steps, always visible */}
      <div className="dial-substeps" aria-hidden={band < 0}>
        {bandStops.map((i) => (
          <button key={i} className={'chip' + (i === value ? ' on' : '')} onClick={() => { ensureAudio(); tick(); buzz(15); onChange(i) }}>{MAG_LABELS[i]}</button>
        ))}
        {band < 0 && <span className="tiny">drag the dial or tap a band</span>}
      </div>
      {/* band jump buttons — thumb zone, below the dial */}
      <div className="dial-bands">
        {BANDS.map((b, k) => (
          <button key={b.label} className={'band' + (k === band ? ' on' : '')} disabled={disabled}
            onClick={() => { ensureAudio(); tick(true); buzz(15); onChange(b.start) }} aria-label={b.name}>{b.label}</button>
        ))}
      </div>
    </div>
  )
}
